import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Spinner, Modal } from 'react-bootstrap';
import {
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { GiDeerHead } from 'react-icons/gi';
import api from '../../../utils/api';
import ClubEventModal from './ClubEventModal';
import { YButton } from '../../../ui/YButton';
import { formatLocalDateLabelFromYMD } from '../../../utils/dateUtils';
import {
  CALENDAR_MONTHS,
  CALENDAR_WEEKDAYS,
  buildUpcomingItems,
  eventLooksAllDay,
  eventLocalNoon,
  formatEventTimeShort,
} from '../calendar/calendarDisplayUtils';
import { dominantSeasonKind, huntSeasonCalendarModel } from '../calendar/huntSeasonCalendar';
import './ClubCalendar.css';
import './clubCalendarFieldEdition.css';

const displayStart = (ev) => (eventLooksAllDay(ev) ? eventLocalNoon(ev) : new Date(ev.occurrence_starts_at || ev.starts_at));
const displayEnd = (ev) => {
  if (eventLooksAllDay(ev)) return eventLocalNoon(ev);
  const end = ev.occurrence_ends_at || ev.ends_at;
  if (!end) return null;
  return new Date(end);
};
const eventKey = (ev) => ev.occurrence_id || `ev-${ev.id}`;

const formatRangeLabel = (ev) => {
  const s = displayStart(ev);
  if (eventLooksAllDay(ev)) {
    return `${s.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} · All day`;
  }
  const e = displayEnd(ev);
  if (!e || s.toDateString() === e.toDateString()) {
    return s.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return `${s.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} – ${e.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
};

function startOfMonth(date) {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfMonth(date) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  d.setHours(23, 59, 59, 999);
  return d;
}

function formatYMD(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function ClubCalendar({ clubId, isPresident, club = null }) {
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);
  const [expandedEventIds, setExpandedEventIds] = useState(() => new Set());
  const [moments, setMoments] = useState([]);
  const [momentsModalDay, setMomentsModalDay] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const monthRange = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return { start, end };
  }, [currentMonth]);

  const fetchEvents = useCallback(async () => {
    if (!clubId) return;
    try {
      setLoading(true);
      setError('');
      const start = formatYMD(monthRange.start);
      const end = formatYMD(monthRange.end);
      const [eventsResp, momentsResp] = await Promise.all([
        api.get(`/clubs/${clubId}/events/?start=${start}&end=${end}`),
        api.get(`/clubs/${clubId}/moments/?start=${start}&end=${end}`),
      ]);
      setEvents(eventsResp.data || []);
      setMoments(momentsResp.data || []);
    } catch {
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [clubId, monthRange]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const seasonModel = useMemo(() => huntSeasonCalendarModel({
    huntZone: club?.hunt_zone,
    huntSeasons: club?.hunt_seasons,
    year: currentMonth.getFullYear(),
    monthIndex: currentMonth.getMonth(),
  }), [club, currentMonth]);

  const daysGrid = useMemo(() => {
    const firstDay = new Date(monthRange.start);
    const lastDay = new Date(monthRange.end);
    const startWeekday = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const grid = [];
    const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;
    for (let i = 0; i < totalCells; i++) {
      const dayNum = i - startWeekday + 1;
      const cellDate = new Date(firstDay);
      cellDate.setDate(dayNum);
      const isInMonth = dayNum >= 1 && dayNum <= daysInMonth;
      const isToday = isInMonth && cellDate.getTime() === today.getTime();
      grid.push({ inMonth: isInMonth, date: cellDate, isToday });
    }
    return grid;
  }, [monthRange]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const start = displayStart(ev);
      const key = formatYMD(start);
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    }
    return map;
  }, [events]);

  const spansByDay = useMemo(() => {
    const map = {};
    const monthStart = new Date(monthRange.start);
    const monthEnd = new Date(monthRange.end);
    monthStart.setHours(0, 0, 0, 0);
    monthEnd.setHours(23, 59, 59, 999);
    for (const ev of events) {
      const s = displayStart(ev);
      const e = displayEnd(ev) || s;
      const startDay = new Date(Math.max(s.getTime(), monthStart.getTime()));
      startDay.setHours(0, 0, 0, 0);
      const endDay = new Date(Math.min(e.getTime(), monthEnd.getTime()));
      endDay.setHours(0, 0, 0, 0);
      let cursor = new Date(startDay);
      while (cursor.getTime() <= endDay.getTime()) {
        const key = formatYMD(cursor);
        if (!map[key]) map[key] = [];
        const spanId = `${ev.id}-${ev.occurrence_id || ev.starts_at}`;
        if (!map[key].some((span) => span.id === spanId)) {
          map[key].push({
            id: spanId,
            title: ev.title,
            isStart: key === formatYMD(startDay),
            isEnd: key === formatYMD(endDay),
          });
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return map;
  }, [events, monthRange]);

  const momentsByDay = useMemo(() => {
    const map = {};
    for (const m of moments) {
      const key = m.date;
      if (!map[key]) map[key] = [];
      map[key].push(m);
    }
    return map;
  }, [moments]);

  const todayYMD = useMemo(() => formatYMD(new Date()), []);

  const upcomingItems = useMemo(
    () =>
      buildUpcomingItems({
        events,
        momentsByDay,
        todayYMD,
        formatYMD,
        displayStart,
        limit: 6,
      }),
    [events, momentsByDay, todayYMD]
  );

  const monthLabel = useMemo(
    () => currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    [currentMonth]
  );

  const openAddForDate = (date) => {
    if (!isPresident) return;
    setPrefillDate(date);
    setEditingEvent(null);
    setShowModal(true);
  };

  const toggleEventExpanded = (id) => {
    setExpandedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onEditEvent = (ev) => {
    const baseStarts = ev.series_starts_at || ev.starts_at;
    const baseEnds = ev.series_ends_at || ev.ends_at;
    setSelectedEvent(null);
    setEditingEvent({ ...ev, starts_at: baseStarts, ends_at: baseEnds });
    setPrefillDate(null);
    setShowModal(true);
  };

  const openAddToday = () => openAddForDate(new Date());

  const handleSaved = async () => {
    setShowModal(false);
    setEditingEvent(null);
    setPrefillDate(null);
    await fetchEvents();
  };

  const goPrev = () => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() - 1);
    setCurrentMonth(startOfMonth(d));
  };

  const goNext = () => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() + 1);
    setCurrentMonth(startOfMonth(d));
  };

  const handleCellClick = (cell, dayEvents) => {
    if (!cell.inMonth) return;
    if (isPresident || dayEvents.length > 0) {
      openAddForDate(cell.date);
    }
  };

  const openHarvestModal = (dayKey, e) => {
    e?.stopPropagation();
    setMomentsModalDay(dayKey);
  };

  const openEventDetail = (ev, e) => {
    e?.stopPropagation();
    setSelectedEvent(ev);
  };

  return (
    <div className="club-calendar-page" data-testid="club-calendar-page">
      <div className="club-calendar-toolbar">
        <div className="club-calendar-toolbar__title">
          <FaCalendarAlt className="club-calendar-toolbar__icon" aria-hidden="true" />
          <h3 className="club-calendar-toolbar__label">Club Calendar</h3>
        </div>
        <div className="club-calendar-toolbar__actions">
          <div className="club-calendar-legend" aria-hidden="true">
            <span className="club-calendar-legend__item">
              <span className="club-calendar-legend__dot club-calendar-legend__dot--event" />
              Event
            </span>
            <span className="club-calendar-legend__item">
              <span className="club-calendar-legend__dot club-calendar-legend__dot--harvest" />
              Harvest
            </span>
          </div>
          <YButton variant="secondary" size="sm" onClick={() => setCurrentMonth(startOfMonth(new Date()))}>
            Today
          </YButton>
          {isPresident && (
            <YButton size="sm" onClick={openAddToday}>
              <FaPlus className="me-1" aria-hidden="true" />
              Add Event
            </YButton>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

      <div className="club-calendar-layout">
        <div className="club-calendar-grid-wrap">
          <div className="club-calendar-grid-card">
            <div className="club-calendar-month-nav">
              <YButton
                variant="secondary"
                size="sm"
                onClick={goPrev}
                aria-label="Previous month"
                className="y-btn-icon"
              >
                <FaChevronLeft />
              </YButton>
              <span className="club-calendar-month-nav__label">{monthLabel}</span>
              <YButton
                variant="secondary"
                size="sm"
                onClick={goNext}
                aria-label="Next month"
                className="y-btn-icon"
              >
                <FaChevronRight />
              </YButton>
            </div>

            <div className="club-calendar-weekdays">
              {CALENDAR_WEEKDAYS.map((d) => (
                <div key={d} className="club-calendar-weekday">
                  {d}
                </div>
              ))}
            </div>

            {loading && events.length === 0 ? (
              <div className="club-calendar-loading">
                <Spinner animation="border" size="sm" className="me-2" />
                <span className="text-muted small">Loading calendar…</span>
              </div>
            ) : (
              <div className="club-calendar-cells">
                {daysGrid.map((cell, idx) => {
                  const key = formatYMD(cell.date);
                  const dayEvents = cell.inMonth ? eventsByDay[key] || [] : [];
                  const spans = cell.inMonth ? spansByDay[key] || [] : [];
                  const dayMoments = cell.inMonth ? momentsByDay[key] || [] : [];
                  const cellClickable = cell.inMonth && (isPresident || dayEvents.length > 0);
                  const seasonKind = cell.inMonth && seasonModel.visible
                    ? dominantSeasonKind(key, club?.hunt_seasons || [])
                    : null;

                  return (
                    <div
                      key={`cell-${idx}`}
                      data-season={seasonKind || undefined}
                      className={[
                        'club-calendar-cell',
                        'calendar-cell',
                        !cell.inMonth ? 'club-calendar-cell--outside inactive' : '',
                        cell.isToday ? 'club-calendar-cell--today today' : '',
                        cellClickable ? 'club-calendar-cell--clickable clickable' : '',
                        seasonKind ? `club-calendar-cell--season-${seasonKind}` : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => handleCellClick(cell, dayEvents)}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && cellClickable) {
                          e.preventDefault();
                          handleCellClick(cell, dayEvents);
                        }
                      }}
                      role={cellClickable ? 'button' : undefined}
                      tabIndex={cellClickable ? 0 : undefined}
                    >
                      {cell.inMonth && (
                        <>
                          <div className="club-calendar-cell__head">
                            <div
                              className={[
                                'club-calendar-day',
                                'calendar-day-number',
                                cell.isToday ? 'club-calendar-day--today' : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                            >
                              {cell.date.getDate()}
                            </div>
                            {dayMoments.length > 0 && (
                              <button
                                type="button"
                                className="club-calendar-harvest calendar-moment-icon"
                                title={`${dayMoments.length} harvest${dayMoments.length > 1 ? 's' : ''}`}
                                aria-label={`${dayMoments.length} harvest${dayMoments.length > 1 ? 's' : ''} on this day`}
                                onClick={(e) => openHarvestModal(key, e)}
                              >
                                <GiDeerHead aria-hidden="true" />
                                {dayMoments.length > 1 && (
                                  <span className="club-calendar-harvest__badge">{dayMoments.length}</span>
                                )}
                              </button>
                            )}
                          </div>
                          <div className="club-calendar-cell__body">
                            {dayEvents.slice(0, 2).map((ev) => (
                              <button
                                key={eventKey(ev)}
                                type="button"
                                className="club-calendar-event-chip"
                                aria-label={ev.title}
                                onClick={(e) => openEventDetail(ev, e)}
                              >
                                <span className="club-calendar-event-chip__title">{ev.title}</span>
                              </button>
                            ))}
                            {dayEvents.length > 2 && (
                              <span className="club-calendar-more">+{dayEvents.length - 2} more</span>
                            )}
                            {spans.length > 0 && (
                              <div className="club-calendar-spans">
                                {spans.slice(0, 2).map((span) => (
                                  <div
                                    key={span.id}
                                    className={[
                                      'club-calendar-span',
                                      span.isStart ? 'club-calendar-span--start' : '',
                                      span.isEnd ? 'club-calendar-span--end' : '',
                                      !span.isStart && !span.isEnd ? 'club-calendar-span--mid' : '',
                                    ]
                                      .filter(Boolean)
                                      .join(' ')}
                                  >
                                    {span.isStart ? span.title : '↔'}
                                  </div>
                                ))}
                                {spans.length > 2 && (
                                  <span className="club-calendar-more">+{spans.length - 2}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {seasonModel.visible && seasonModel.legend.length > 0 ? (
              <ul className="club-calendar-season-legend" data-testid="club-calendar-season-legend">
                {seasonModel.legend.map((item) => (
                  <li key={`${item.kind}-${item.text}`} className="club-calendar-season-legend__item">
                    <span className={`club-calendar-season-swatch club-calendar-season-swatch--${item.kind}`} />
                    {item.text}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        <aside className="club-calendar-upcoming" aria-label="Upcoming">
          <div className="club-calendar-upcoming__band">Upcoming</div>
          {upcomingItems.length === 0 && !loading && (
            <p className="club-calendar-upcoming__empty text-muted small mb-0">No upcoming items this month.</p>
          )}
          <ul className="club-calendar-upcoming__list">
            {upcomingItems.map((item) => {
              if (item.kind === 'harvest') {
                const [, month, day] = item.day.split('-').map(Number);
                const mon = CALENDAR_MONTHS[month - 1].slice(0, 3).toUpperCase();
                return (
                  <li key={`harvest-${item.day}`}>
                    <button
                      type="button"
                      className="club-calendar-upcoming-card club-calendar-upcoming-card--harvest"
                      onClick={() => setMomentsModalDay(item.day)}
                    >
                      <span className="club-calendar-upcoming-card__date" aria-hidden="true">
                        <span className="club-calendar-upcoming-card__mon">{mon}</span>
                        <span className="club-calendar-upcoming-card__day">{day}</span>
                      </span>
                      <span className="club-calendar-upcoming-card__meta">
                        <span className="club-calendar-upcoming-card__title">
                          {item.moments.length} harvest{item.moments.length === 1 ? '' : 's'}
                        </span>
                        <span className="club-calendar-upcoming-card__sub">
                          {mon} {day}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              }
              const ev = item.ev;
              const start = displayStart(ev);
              const [, month, day] = item.sortKey.split('-').map(Number);
              const mon = CALENDAR_MONTHS[month - 1].slice(0, 3).toUpperCase();
              return (
                <li key={eventKey(ev)}>
                  <button
                    type="button"
                    className="club-calendar-upcoming-card"
                    onClick={() => setSelectedEvent(ev)}
                  >
                    <span className="club-calendar-upcoming-card__date club-calendar-upcoming-card__date--event" aria-hidden="true">
                      <span className="club-calendar-upcoming-card__mon">{mon}</span>
                      <span className="club-calendar-upcoming-card__day">{day}</span>
                    </span>
                    <span className="club-calendar-upcoming-card__meta">
                      <span className="club-calendar-upcoming-card__title">{ev.title}</span>
                      <span className="club-calendar-upcoming-card__sub">
                        {mon} {day}
                        {eventLooksAllDay(ev) ? ' · All day' : formatEventTimeShort(start) ? ` · ${formatEventTimeShort(start)}` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>
      </div>

      {seasonModel.visible ? (
        <section className="club-calendar-seasons" data-testid="club-calendar-seasons" aria-label="State deer seasons">
          <h4 className="club-calendar-seasons__title">{seasonModel.title}</h4>
          <table className="club-calendar-seasons__table">
            <thead>
              <tr>
                <th>Season</th>
                <th>Sex</th>
                <th>Dates</th>
              </tr>
            </thead>
            <tbody>
              {seasonModel.rows.map((row) => (
                <tr key={`${row.kind}-${row.dates}-${row.sexLabel}`}>
                  <td>{row.kindLabel}</td>
                  <td>{row.sexLabel}</td>
                  <td>{row.dates}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="club-calendar-seasons__note">
            {seasonModel.disclaimer}
            {seasonModel.officialUrl ? (
              <>
                {' '}
                <a href={seasonModel.officialUrl} target="_blank" rel="noreferrer">
                  Outdoor Alabama
                </a>
              </>
            ) : null}
          </p>
        </section>
      ) : null}

      <section className="club-calendar-month-stream" aria-labelledby="club-calendar-month-stream-title">
        <h4 id="club-calendar-month-stream-title" className="club-calendar-month-stream__title">
          {monthLabel} events
        </h4>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            <p className="club-calendar-month-stream__meta text-muted small">
              Showing {Object.keys(eventsByDay).length} day(s) with events
            </p>
            {Object.keys(eventsByDay).length === 0 && Object.keys(momentsByDay).length === 0 && (
              <p className="text-muted small mb-0">No events or harvests this month.</p>
            )}
            {Object.entries(eventsByDay)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([day, evs]) => (
                <div key={day} className="club-calendar-day-group">
                  <div className="club-calendar-day-group__title">
                    <FaCalendarAlt className="me-2" aria-hidden="true" />
                    {formatLocalDateLabelFromYMD(day)}
                  </div>
                  {(momentsByDay[day] || []).length > 0 && (
                    <div className="club-calendar-stream-item">
                      <button
                        type="button"
                        className="club-calendar-stream-item__head club-calendar-stream-item__head--harvest"
                        onClick={() => setMomentsModalDay(day)}
                      >
                        <GiDeerHead aria-hidden="true" />
                        Harvests ({momentsByDay[day].length})
                      </button>
                    </div>
                  )}
                  {evs.map((ev) => (
                    <div key={eventKey(ev)} className="club-calendar-stream-item">
                      <button
                        type="button"
                        className="club-calendar-stream-item__head"
                        aria-expanded={expandedEventIds.has(eventKey(ev))}
                        onClick={() => toggleEventExpanded(eventKey(ev))}
                      >
                        <span className="club-calendar-stream-item__title">{ev.title}</span>
                        <span className="club-calendar-stream-item__meta">
                          <FaClock className="me-1" aria-hidden="true" />
                          {displayStart(ev).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {displayEnd(ev) &&
                          displayEnd(ev).toDateString() !== displayStart(ev).toDateString() ? (
                            <span className="ms-2 badge rounded-pill bg-light text-muted">Multi-day</span>
                          ) : null}
                          {ev.location_name ? (
                            <>
                              <span className="mx-2">·</span>
                              <FaMapMarkerAlt className="me-1" aria-hidden="true" />
                              {ev.location_name}
                            </>
                          ) : null}
                        </span>
                      </button>
                      {expandedEventIds.has(eventKey(ev)) && (
                        <div className="club-calendar-stream-item__details">
                          <div className="club-calendar-stream-detail">
                            <FaCalendarAlt className="me-2" aria-hidden="true" />
                            {formatRangeLabel(ev)}
                          </div>
                          {ev.location_name ? (
                            <div className="club-calendar-stream-detail">
                              <FaMapMarkerAlt className="me-2" aria-hidden="true" />
                              {ev.location_name}
                            </div>
                          ) : null}
                          {ev.description ? (
                            <p className="club-calendar-stream-desc">{ev.description}</p>
                          ) : null}
                          {isPresident && (
                            <div className="club-calendar-stream-actions">
                              <YButton variant="secondary" size="sm" onClick={() => onEditEvent(ev)}>
                                Edit
                              </YButton>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            {Object.keys(eventsByDay).length === 0 &&
              Object.entries(momentsByDay)
                .filter(([day]) => !eventsByDay[day])
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([day, dayMoments]) => (
                  <div key={`moments-only-${day}`} className="club-calendar-day-group">
                    <div className="club-calendar-day-group__title">
                      <FaCalendarAlt className="me-2" aria-hidden="true" />
                      {formatLocalDateLabelFromYMD(day)}
                    </div>
                    <div className="club-calendar-stream-item">
                      <button
                        type="button"
                        className="club-calendar-stream-item__head club-calendar-stream-item__head--harvest"
                        onClick={() => setMomentsModalDay(day)}
                      >
                        <GiDeerHead aria-hidden="true" />
                        Harvests ({dayMoments.length})
                      </button>
                    </div>
                  </div>
                ))}
          </>
        )}
      </section>

      <ClubEventModal
        show={showModal}
        onHide={() => setShowModal(false)}
        clubId={clubId}
        event={editingEvent}
        prefillDate={prefillDate}
        onSaved={handleSaved}
        canEdit={isPresident}
      />

      <Modal show={!!selectedEvent} onHide={() => setSelectedEvent(null)} centered dialogClassName="club-calendar-dialog">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{selectedEvent?.title || 'Event'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <div className="club-calendar-event-detail">
              <div className="club-calendar-event-detail__row">
                <FaCalendarAlt className="me-2 text-muted" aria-hidden="true" />
                {formatRangeLabel(selectedEvent)}
              </div>
              {selectedEvent.location_name ? (
                <div className="club-calendar-event-detail__row">
                  <FaMapMarkerAlt className="me-2 text-muted" aria-hidden="true" />
                  {selectedEvent.location_name}
                </div>
              ) : null}
              {selectedEvent.description ? (
                <p className="club-calendar-event-detail__desc">{selectedEvent.description}</p>
              ) : null}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <YButton size="sm" variant="secondary" onClick={() => setSelectedEvent(null)}>
            Close
          </YButton>
          {isPresident && selectedEvent && (
            <YButton
              size="sm"
              onClick={() => {
                onEditEvent(selectedEvent);
              }}
            >
              Edit event
            </YButton>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={!!momentsModalDay} onHide={() => setMomentsModalDay(null)} centered dialogClassName="club-calendar-dialog">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {momentsModalDay ? `Harvests · ${formatLocalDateLabelFromYMD(momentsModalDay)}` : 'Harvests'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(momentsByDay[momentsModalDay] || []).map((m) => (
            <div key={m.id} className="d-flex align-items-center mb-2">
              <GiDeerHead className="me-2 text-success" aria-hidden="true" />
              <div>
                <div className="fw-semibold">{m.member || 'Member'}</div>
                <div className="text-muted small">
                  {m.deer_type === 'buck' && m.points != null
                    ? `${m.points}-pt buck`
                    : m.deer_type || 'Harvest'}
                </div>
              </div>
            </div>
          ))}
          {(momentsByDay[momentsModalDay] || []).length === 0 && (
            <div className="text-muted small">No harvests for this day.</div>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <YButton size="sm" variant="secondary" onClick={() => setMomentsModalDay(null)}>
            Close
          </YButton>
          <YButton
            size="sm"
            onClick={() => {
              window.location.href = `/clubs/${clubId}`;
            }}
          >
            View Deer Log
          </YButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

ClubCalendar.propTypes = {
  clubId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isPresident: PropTypes.bool,
  club: PropTypes.object,
};
